/**
 * Relationship type diagrams — dark-mode SVG illustrations
 * using the Peak Priorities visual design system.
 *
 * Background: #0B0F19 · Surface: #111827 · Rule: #1A2234
 * Text-pri: #D8DDE8 · Text-sec: #9BA5BC · Text-ter: #6B7A96
 * Accent colors are semantic per the design system.
 */

const T = {
  bg: '#0B0F19',
  surface: '#111827',
  surfaceRaised: '#0D1220',
  rule: '#1A2234',
  ruleBright: '#222D40',
  textPri: '#D8DDE8',
  textSec: '#9BA5BC',
  textTer: '#6B7A96',
  slate: '#3D4B6F',
  slateBright: '#5B71A8',
  mustard: '#D4A017',
  mustardDim: '#7A5C0C',
  coral: '#C0533A',
  coralDim: '#6B2D1E',
  green: '#3A7D56',
  greenDim: '#1D4232',
};

// ── Shared primitives ─────────────────────────────────────────────────────────

function Box({
  x, y, w = 140, h = 44, label, sub, accent = T.slate, dim = false,
}: {
  x: number; y: number; w?: number; h?: number;
  label: string; sub?: string; accent?: string; dim?: boolean;
}) {
  const fill = dim ? '#090D14' : T.surface;
  const stroke = dim ? '#1E2A3A' : accent;
  const lc = dim ? T.textTer : T.textPri;
  const sc = dim ? T.textTer : T.textSec;
  const sw = dim ? 0.8 : 1.4;
  const cy = sub ? y + h * 0.40 : y + h * 0.58;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="6"
        fill={fill} stroke={stroke} strokeWidth={sw}
      />
      <text x={x + w / 2} y={cy}
        textAnchor="middle" fontSize="12" fontFamily="monospace"
        fill={lc} fontWeight="600" letterSpacing="0.5"
      >{label}</text>
      {sub && (
        <text x={x + w / 2} y={y + h * 0.74}
          textAnchor="middle" fontSize="10" fontFamily="monospace" fill={sc}
        >{sub}</text>
      )}
    </g>
  );
}

function Arrowhead({ id, color }: { id: string; color: string }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3.5" orient="auto">
      <path d="M0,0 L0,7 L8,3.5 z" fill={color} />
    </marker>
  );
}

// ── Cluster ───────────────────────────────────────────────────────────────────

export function DiagramCluster() {
  const W = 560; const H = 200;
  // three source items on left, one cluster node on right
  const items = [
    { y: 20,  label: 'PROBLEM A', accent: T.slateBright },
    { y: 82,  label: 'PROBLEM B', accent: T.slate, dim: true },
    { y: 144, label: 'PROBLEM C', accent: T.slate, dim: true },
  ];
  const midY = [42, 104, 166];
  const cxLeft = 150;
  const clusterX = 310; const clusterY = 70; const clusterW = 200; const clusterH = 60;
  const clusterMidY = clusterY + clusterH / 2;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />

      {items.map((it, i) => (
        <g key={i}>
          <Box x={10} y={it.y} w={cxLeft} h={42} label={it.label}
            accent={it.accent} dim={it.dim}
          />
          <line
            x1={cxLeft + 10} y1={midY[i]}
            x2={clusterX - 4} y2={clusterMidY}
            stroke={T.slateBright} strokeWidth="1.2"
            strokeDasharray="4,3" opacity="0.55"
          />
          <text x={(cxLeft + 10 + clusterX) / 2 - 10} y={midY[i] - 6}
            textAnchor="middle" fontSize="12" fill={T.slateBright} opacity="0.6"
          >≈</text>
        </g>
      ))}

      {/* cluster node */}
      <rect x={clusterX} y={clusterY} width={clusterW} height={clusterH} rx="8"
        fill={T.surfaceRaised} stroke={T.slateBright} strokeWidth="1.8"
      />
      <text x={clusterX + clusterW / 2} y={clusterY + 28}
        textAnchor="middle" fontSize="13" fontFamily="monospace"
        fill={T.textPri} fontWeight="700" letterSpacing="1.5"
      >CLUSTER</text>
      <text x={clusterX + clusterW / 2} y={clusterY + 46}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.slateBright}
      >3 members · same underlying entity</text>
    </svg>
  );
}

// ── Package ───────────────────────────────────────────────────────────────────

export function DiagramPackage() {
  const W = 560; const H = 200;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />

      {/* eyebrow */}
      <text x={W / 2} y={22}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.mustard} letterSpacing="2" opacity="0.8"
      >PACKAGE · ALL OR NONE</text>

      {/* outer dashed boundary */}
      <rect x={20} y={30} width={W - 40} height={148} rx="10"
        fill="none" stroke={T.mustard} strokeWidth="1.5" strokeDasharray="8,4"
        opacity="0.6"
      />

      {/* four items in 2×2 */}
      <Box x={40}  y={48} w={220} h={44} label="ITEM A" accent={T.mustard} />
      <Box x={300} y={48} w={220} h={44} label="ITEM B" accent={T.mustard} />
      <Box x={40}  y={116} w={220} h={44} label="ITEM C" accent={T.mustard} />
      <Box x={300} y={116} w={220} h={44} label="ITEM D" accent={T.mustardDim} dim />

      {/* center join symbol */}
      <text x={W / 2} y={103}
        textAnchor="middle" fontSize="18" fill={T.mustard} opacity="0.5"
      >⊕</text>
    </svg>
  );
}

// ── Exclusive (Competing) ───────────────────────────────────────────────────────

export function DiagramExclusive() {
  const W = 560; const H = 200;
  const boxW = 200; const boxH = 52; const boxY = 74;
  const aX = 40; const bX = W - boxW - 40;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />

      {/* eyebrow */}
      <text x={W / 2} y={22}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.coral} letterSpacing="2" opacity="0.85"
      >COMPETING · ONE OR THE OTHER, NOT BOTH</text>

      {/* the two competing items — A committed, B foreclosed */}
      <Box x={aX} y={boxY} w={boxW} h={boxH}
        label="ITEM A" sub="committed" accent={T.coral}
      />
      <Box x={bX} y={boxY} w={boxW} h={boxH}
        label="ITEM B" sub="foreclosed" accent={T.coralDim} dim
      />

      {/* XOR symbol between them */}
      <circle cx={W / 2} cy={boxY + boxH / 2} r="18"
        fill={T.surface} stroke={T.coral} strokeWidth="1.5"
      />
      <text x={W / 2} y={boxY + boxH / 2 + 6}
        textAnchor="middle" fontSize="18" fill={T.coral} fontFamily="monospace"
      >⊻</text>

      {/* strike line through foreclosed item */}
      <line x1={bX + 8} y1={boxY + boxH / 2} x2={bX + boxW - 8} y2={boxY + boxH / 2}
        stroke={T.coral} strokeWidth="1.2" opacity="0.5" strokeDasharray="4,3"
      />

      <text x={W / 2} y={boxY + boxH + 38}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="1"
      >AT MOST ONE MAY BE COMMITTED · HORIZONTAL</text>
    </svg>
  );
}

// ── Dependency ────────────────────────────────────────────────────────────────

export function DiagramDependency() {
  const W = 560; const H = 180;
  const boxW = 160; const boxH = 52; const boxY = 64;
  const aX = 30; const bX = W - boxW - 30;
  const arrowY = boxY + boxH / 2;
  const midX = (aX + boxW + bX) / 2;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs><Arrowhead id="ah-dep-coral" color={T.coral} /></defs>

      {/* eyebrow */}
      <text x={W / 2} y={18}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="2"
      >DOWNSTREAM PERSPECTIVE · HORIZONTAL</text>

      {/* perspective badge on B */}
      <rect x={bX - 2} y={boxY - 20} width={boxW + 4} height={16} rx="3"
        fill={T.surfaceRaised} stroke={T.coralDim} strokeWidth="0.8"
      />
      <text x={bX + boxW / 2} y={boxY - 7}
        textAnchor="middle" fontSize="9" fontFamily="monospace" fill={T.coral}
        letterSpacing="1"
      >ASSERTING ITEM</text>

      <Box x={aX} y={boxY} w={boxW} h={boxH}
        label="ITEM A" sub="must resolve first" accent={T.coral}
      />

      {/* arrow */}
      <line x1={aX + boxW + 6} y1={arrowY} x2={bX - 6} y2={arrowY}
        stroke={T.coral} strokeWidth="2"
        markerEnd="url(#ah-dep-coral)"
        opacity="0.9"
      />
      {/* "blocks" pill */}
      <rect x={midX - 34} y={arrowY - 14} width={68} height={20} rx="4"
        fill={T.surfaceRaised} stroke={T.coralDim} strokeWidth="0.8"
      />
      <text x={midX} y={arrowY}
        textAnchor="middle" fontSize="11" fontFamily="monospace" fill={T.coral}
      >blocks</text>

      <Box x={bX} y={boxY} w={boxW} h={boxH}
        label="ITEM B" sub="waits on Item A" accent={T.coral} dim={false}
      />

      {/* footnote */}
      <text x={W / 2} y={H - 10}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Item B declares: "I depend on A" · mirror of Prerequisite</text>
    </svg>
  );
}

// ── Aggregation ───────────────────────────────────────────────────────────────

export function DiagramAggregation() {
  const W = 560; const H = 220;
  const parentW = 180; const parentX = (W - parentW) / 2; const parentY = 30; const parentH = 48;
  const parentMidX = parentX + parentW / 2;
  const parentBotY = parentY + parentH;

  const children = [
    { x: 30,  label: 'CHILD 1', sub: 'constituent' },
    { x: 210, label: 'CHILD 2', sub: 'constituent' },
    { x: 390, label: 'CHILD 3', sub: 'constituent' },
  ];
  const childW = 140; const childY = 144; const childH = 46;
  const branchY = parentBotY + 42; // horizontal rail — between parent and children

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs><Arrowhead id="ah-agg-up" color={T.green} /></defs>

      {/* eyebrow */}
      <text x={W / 2} y={18}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="2"
      >BOTTOM-UP COMPOSITION · VERTICAL</text>

      <Box x={parentX} y={parentY} w={parentW} h={parentH}
        label="PARENT ITEM" sub="higher altitude" accent={T.green}
      />

      {/* trunk: from branch rail UP to parent — arrow points up */}
      <line x1={parentMidX} y1={branchY} x2={parentMidX} y2={parentBotY + 2}
        stroke={T.green} strokeWidth="1.8" markerEnd="url(#ah-agg-up)" opacity="0.85"
      />

      {/* horizontal rail connecting all children to trunk */}
      {(() => {
        const leftMidX  = children[0].x + childW / 2;
        const rightMidX = children[children.length - 1].x + childW / 2;
        return (
          <line x1={leftMidX} y1={branchY} x2={rightMidX} y2={branchY}
            stroke={T.green} strokeWidth="1.6" opacity="0.6"
          />
        );
      })()}

      {/* vertical stems from each child up to rail, with upward arrowheads */}
      {children.map((c) => {
        const cMidX = c.x + childW / 2;
        return (
          <g key={c.label}>
            <line x1={cMidX} y1={childY - 2} x2={cMidX} y2={branchY + 2}
              stroke={T.green} strokeWidth="1.6" markerEnd="url(#ah-agg-up)" opacity="0.75"
            />
            <Box x={c.x} y={childY} w={childW} h={childH}
              label={c.label} sub={c.sub} accent={T.green}
            />
          </g>
        );
      })}

      {/* footnote */}
      <text x={W / 2} y={H - 8}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Child asserts upward · scoring rolls up · counterpart to Breakdown</text>
    </svg>
  );
}

// ── Elaboration ───────────────────────────────────────────────────────────────

export function DiagramElaboration() {
  const W = 560; const H = 220;
  const parentW = 220; const parentH = 46;
  const parentX = (W - parentW) / 2; const parentY = 18;
  const parentMidX = parentX + parentW / 2;
  const parentBotY = parentY + parentH;
  const forkY = parentBotY + 36;
  const childY = forkY + 34;
  const childH = 52;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs>
        <Arrowhead id="ah-elab-l" color={T.slate} />
        <Arrowhead id="ah-elab-r" color={T.mustard} />
      </defs>

      {/* parent need */}
      <rect x={parentX} y={parentY} width={parentW} height={parentH} rx="6"
        fill={T.surface} stroke={T.slateBright} strokeWidth="1.6"
      />
      <text x={parentMidX} y={parentY + 20}
        textAnchor="middle" fontSize="12" fontFamily="monospace"
        fill={T.textPri} fontWeight="700"
      >REDUCE PAGE LOAD TIME</text>
      <text x={parentMidX} y={parentY + 36}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textSec}
      >parent need</text>

      {/* trunk */}
      <line x1={parentMidX} y1={parentBotY} x2={parentMidX} y2={forkY}
        stroke={T.slateBright} strokeWidth="1.4" opacity="0.7"
      />

      {/* fork symbol */}
      <circle cx={parentMidX} cy={forkY} r={10}
        fill={T.surfaceRaised} stroke={T.slateBright} strokeWidth="1.4"
      />
      <text x={parentMidX} y={forkY + 5}
        textAnchor="middle" fontSize="12" fontFamily="monospace" fill={T.slateBright}
      >⑂</text>

      {/* left branch: quick win (dim = lower investment) */}
      <line x1={parentMidX - 10} y1={forkY + 10} x2={120} y2={childY - 4}
        stroke={T.slate} strokeWidth="1.4" markerEnd="url(#ah-elab-l)" opacity="0.7"
      />
      <rect x={20} y={childY} width={200} height={childH} rx="6"
        fill={T.surface} stroke={T.slate} strokeWidth="1.2" opacity="0.75"
      />
      <text x={120} y={childY + 22}
        textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill={T.textSec} fontWeight="600"
      >CDN CACHING</text>
      <text x={120} y={childY + 38}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >quick win · low investment</text>

      {/* right branch: platform bet (bright = higher ambition) */}
      <line x1={parentMidX + 10} y1={forkY + 10} x2={W - 120} y2={childY - 4}
        stroke={T.mustard} strokeWidth="1.4" markerEnd="url(#ah-elab-r)" opacity="0.85"
      />
      <rect x={W - 220} y={childY} width={200} height={childH} rx="6"
        fill={T.surface} stroke={T.mustard} strokeWidth="1.4"
      />
      <text x={W - 120} y={childY + 22}
        textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill={T.textPri} fontWeight="600"
      >ASSET PIPELINE OVERHAUL</text>
      <text x={W - 120} y={childY + 38}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.mustard}
      >platform bet · high ambition</text>

      {/* subtype labels */}
      <text x={120} y={childY + childH + 18}
        textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill={T.textTer} letterSpacing="0.5"
      >Solution Variant</text>
      <text x={W - 120} y={childY + childH + 18}
        textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill={T.mustard} opacity="0.7" letterSpacing="0.5"
      >Solution Spectrum</text>
    </svg>
  );
}

// ── Reframe ───────────────────────────────────────────────────────────────────

export function DiagramReframe() {
  const W = 560; const H = 210;
  const centerX = 200; const centerY = 80; const centerW = 160; const centerH = 50;
  const cx = centerX + centerW / 2; const cy = centerY + centerH / 2;

  const frames = [
    { x: 380, y: 20,  w: 150, label: 'Risk category',      color: T.mustard },
    { x: 380, y: 90,  w: 150, label: 'Opportunity category',color: T.green   },
    { x: 380, y: 160, w: 150, label: 'Problem category',   color: T.coral   },
  ];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />

      {/* primary frame badge */}
      <rect x={20} y={80} width={154} height={50} rx="6"
        fill={T.surfaceRaised} stroke={T.ruleBright} strokeWidth="0.8"
      />
      <text x={97} y={101} textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer}
      >★ PRIMARY</text>
      <text x={97} y={118} textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer}
      >Problem category</text>

      {/* center item */}
      <rect x={centerX} y={centerY} width={centerW} height={centerH} rx="8"
        fill={T.surface} stroke={T.mustard} strokeWidth="1.8"
      />
      <text x={cx} y={cy - 6}
        textAnchor="middle" fontSize="13" fontFamily="monospace"
        fill={T.textPri} fontWeight="700"
      >ONE ITEM</text>
      <text x={cx} y={cy + 12}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.mustard}
      >multiple categories</text>

      {/* frame pills */}
      {frames.map((f) => {
        const pillCy = f.y + 20;
        return (
          <g key={f.label}>
            <rect x={f.x} y={f.y} width={f.w} height={40} rx="20"
              fill={T.surfaceRaised} stroke={f.color} strokeWidth="1.2"
            />
            <text x={f.x + f.w / 2} y={f.y + 24}
              textAnchor="middle" fontSize="11" fontFamily="monospace" fill={f.color}
            >{f.label}</text>
            <line
              x1={centerX + centerW - 2} y1={cy}
              x2={f.x + 2} y2={pillCy}
              stroke={f.color} strokeWidth="1" strokeDasharray="4,3" opacity="0.6"
            />
          </g>
        );
      })}
    </svg>
  );
}

// ── Lineage ───────────────────────────────────────────────────────────────────

export function DiagramLineage() {
  const W = 600; const H = 200;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs>
        <Arrowhead id="ah-green" color={T.green} />
        <Arrowhead id="ah-slate-lg" color={T.slate} />
      </defs>

      {/* ORIGIN */}
      <Box x={20} y={80} w={120} h={44} label="ORIGIN" sub="v1" accent={T.green} />

      {/* arrow to fork */}
      <line x1={140} y1={102} x2={186} y2={102}
        stroke={T.green} strokeWidth="1.6" markerEnd="url(#ah-green)" opacity="0.85"
      />

      {/* fork circle */}
      <circle cx={204} cy={102} r={18}
        fill={T.surfaceRaised} stroke={T.green} strokeWidth="1.6"
      />
      <text x={204} y={108}
        textAnchor="middle" fontSize="15" fontFamily="monospace" fill={T.green}
      >⑂</text>
      <text x={204} y={136}
        textAnchor="middle" fontSize="9" fontFamily="monospace"
        fill={T.textTer} letterSpacing="1"
      >SPLIT</text>

      {/* upper branch */}
      <line x1={222} y1={90} x2={286} y2={50}
        stroke={T.green} strokeWidth="1.6" markerEnd="url(#ah-green)" opacity="0.8"
      />
      <Box x={286} y={28} w={130} h={44} label="FORK A" sub="continues" accent={T.green} />

      {/* lower branch */}
      <line x1={222} y1={114} x2={286} y2={152}
        stroke={T.green} strokeWidth="1.6" markerEnd="url(#ah-green)" opacity="0.8"
      />
      <Box x={286} y={130} w={130} h={44} label="FORK B" sub="new scope" accent={T.green} />

      {/* Fork A supersedes */}
      <line x1={416} y1={50} x2={490} y2={50}
        stroke={T.slate} strokeWidth="1.4" markerEnd="url(#ah-slate-lg)"
        strokeDasharray="5,3" opacity="0.75"
      />
      <text x={453} y={43}
        textAnchor="middle" fontSize="9" fontFamily="monospace" fill={T.textTer}
      >supersedes</text>
      <Box x={490} y={28} w={90} h={44} label="FORK A-2" sub="v2" accent={T.slate} dim />

      {/* footnote */}
      <text x={W / 2} y={H - 10}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Events: split · merge · fork · supersede · retire</text>
    </svg>
  );
}

// ── Breakdown ─────────────────────────────────────────────────────────────────

export function DiagramBreakdown() {
  const W = 560; const H = 220;
  const parentW = 180; const parentH = 48;
  const parentX = (W - parentW) / 2; const parentY = 30;
  const parentMidX = parentX + parentW / 2;
  const parentBotY = parentY + parentH;
  const branchY = parentBotY + 42; // horizontal rail — same position as Aggregation
  const childY = branchY + 30;
  const childW = 140; const childH = 46;

  const children = [
    { x: 30,  label: 'PART 1', sub: 'constituent' },
    { x: 210, label: 'PART 2', sub: 'constituent' },
    { x: 390, label: 'PART 3', sub: 'constituent' },
  ];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs><Arrowhead id="ah-bd-down" color={T.green} /></defs>

      {/* eyebrow */}
      <text x={W / 2} y={18}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="2"
      >TOP-DOWN DECOMPOSITION · VERTICAL</text>

      <Box x={parentX} y={parentY} w={parentW} h={parentH}
        label="PARENT ITEM" sub="defines its parts" accent={T.green}
      />

      {/* trunk: from parent DOWN to branch rail — arrow points down */}
      <line x1={parentMidX} y1={parentBotY + 2} x2={parentMidX} y2={branchY - 2}
        stroke={T.green} strokeWidth="1.8" markerEnd="url(#ah-bd-down)" opacity="0.85"
      />

      {/* horizontal rail */}
      {(() => {
        const leftMidX  = children[0].x + childW / 2;
        const rightMidX = children[children.length - 1].x + childW / 2;
        return (
          <line x1={leftMidX} y1={branchY} x2={rightMidX} y2={branchY}
            stroke={T.green} strokeWidth="1.6" opacity="0.6"
          />
        );
      })()}

      {/* vertical stems from rail DOWN to each child, with downward arrowheads */}
      {children.map((c) => {
        const cMidX = c.x + childW / 2;
        return (
          <g key={c.label}>
            <line x1={cMidX} y1={branchY + 2} x2={cMidX} y2={childY - 2}
              stroke={T.green} strokeWidth="1.6" markerEnd="url(#ah-bd-down)" opacity="0.75"
            />
            <Box x={c.x} y={childY} w={childW} h={childH}
              label={c.label} sub={c.sub} accent={T.green}
            />
          </g>
        );
      })}

      {/* footnote */}
      <text x={W / 2} y={H - 8}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Parent defines its parts · asserted top-down · counterpart to Aggregation</text>
    </svg>
  );
}

// ── Contains ──────────────────────────────────────────────────────────────────

export function DiagramContains() {
  const W = 560; const H = 220;
  const sky = '#0ea5e9';
  const parentW = 180; const parentH = 48;
  const parentX = (W - parentW) / 2; const parentY = 30;
  const parentMidX = parentX + parentW / 2;
  const parentBotY = parentY + parentH;
  const branchY = parentBotY + 42;
  const childY = branchY + 30;
  const childW = 140; const childH = 46;

  const children = [
    { x: 30, label: 'STORY A', sub: 'child item' },
    { x: 210, label: 'STORY B', sub: 'child item' },
    { x: 390, label: 'STORY C', sub: 'child item' },
  ];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs><Arrowhead id="ah-con-down" color={sky} /></defs>

      <text x={W / 2} y={18}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="2"
      >PASSIVE HIERARCHY · REPORTING ONLY</text>

      <Box x={parentX} y={parentY} w={parentW} h={parentH}
        label="PARENT / EPIC" sub="thematic container" accent={sky}
      />

      <line x1={parentMidX} y1={parentBotY + 2} x2={parentMidX} y2={branchY - 2}
        stroke={sky} strokeWidth="1.8" markerEnd="url(#ah-con-down)" opacity="0.85"
      />

      {(() => {
        const leftMidX = children[0].x + childW / 2;
        const rightMidX = children[children.length - 1].x + childW / 2;
        return (
          <line x1={leftMidX} y1={branchY} x2={rightMidX} y2={branchY}
            stroke={sky} strokeWidth="1.6" opacity="0.6"
          />
        );
      })()}

      {children.map((c) => {
        const cMidX = c.x + childW / 2;
        return (
          <g key={c.label}>
            <line x1={cMidX} y1={branchY + 2} x2={cMidX} y2={childY - 2}
              stroke={sky} strokeWidth="1.6" markerEnd="url(#ah-con-down)" opacity="0.75"
            />
            <Box x={c.x} y={childY} w={childW} h={childH}
              label={c.label} sub={c.sub} accent={sky}
            />
          </g>
        );
      })}

      <text x={W / 2} y={H - 8}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >No gate weight · navigation & reporting · distinct from Breakdown</text>
    </svg>
  );
}

// ── Prerequisite ──────────────────────────────────────────────────────────────

export function DiagramPrerequisite() {
  const W = 560; const H = 180;
  const boxW = 160; const boxH = 52; const boxY = 64;
  const aX = 30; const bX = W - boxW - 30;
  const arrowY = boxY + boxH / 2;
  const midX = (aX + boxW + bX) / 2;
  const sky = '#0284C7'; const skyDim = '#0C4A6E';

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />
      <defs><Arrowhead id="ah-pre-sky" color={sky} /></defs>

      {/* eyebrow */}
      <text x={W / 2} y={18}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="2"
      >UPSTREAM PERSPECTIVE · HORIZONTAL</text>

      {/* perspective badge on A */}
      <rect x={aX - 2} y={boxY - 20} width={boxW + 4} height={16} rx="3"
        fill={T.surfaceRaised} stroke={skyDim} strokeWidth="0.8"
      />
      <text x={aX + boxW / 2} y={boxY - 7}
        textAnchor="middle" fontSize="9" fontFamily="monospace" fill={sky}
        letterSpacing="1"
      >ASSERTING ITEM</text>

      <Box x={aX} y={boxY} w={boxW} h={boxH}
        label="ITEM A" sub="must resolve first" accent={sky}
      />

      {/* arrow */}
      <line x1={aX + boxW + 6} y1={arrowY} x2={bX - 6} y2={arrowY}
        stroke={sky} strokeWidth="2"
        markerEnd="url(#ah-pre-sky)"
        opacity="0.9"
      />
      {/* "unlocks" pill */}
      <rect x={midX - 36} y={arrowY - 14} width={72} height={20} rx="4"
        fill={T.surfaceRaised} stroke={skyDim} strokeWidth="0.8"
      />
      <text x={midX} y={arrowY}
        textAnchor="middle" fontSize="11" fontFamily="monospace" fill={sky}
      >unlocks</text>

      <Box x={bX} y={boxY} w={boxW} h={boxH}
        label="ITEM B" sub="can now proceed" accent={sky} dim={false}
      />

      {/* footnote */}
      <text x={W / 2} y={H - 10}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Item A declares: "I am a prerequisite for B" · mirror of Dependency</text>
    </svg>
  );
}

// ── Collections ───────────────────────────────────────────────────────────────

export function DiagramCollections() {
  const W = 560; const H = 180;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }}>
      <rect x={0} y={0} width={W} height={H} fill={T.bg} />

      {/* folder tab */}
      <path d="M20 40 L20 154 L440 154 L440 54 L200 54 L180 40 Z"
        fill={T.surfaceRaised} stroke={T.slateBright} strokeWidth="1.4"
      />
      <line x1={20} y1={68} x2={440} y2={68} stroke={T.ruleBright} strokeWidth="1" />

      {/* folder label */}
      <text x={110} y={59}
        textAnchor="middle" fontSize="11" fontFamily="monospace"
        fill={T.slateBright} fontWeight="600"
      >Q4 Research Themes</text>

      {/* items inside */}
      <Box x={34}  y={78} w={120} h={60} label="ITEM A" sub="risk analysis" accent={T.slate} />
      <Box x={168} y={78} w={120} h={60} label="ITEM B" sub="market fit" accent={T.slate} />
      <Box x={302} y={78} w={120} h={60} label="ITEM C" sub="+ 4 more" accent={T.slate} dim />

      {/* right side label */}
      <text x={490} y={96}
        textAnchor="middle" fontSize="10" fontFamily="monospace"
        fill={T.textTer} letterSpacing="0.5"
      >Scratchpad.</text>
      <text x={490} y={112}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >Not a pool.</text>
      <text x={490} y={130}
        textAnchor="middle" fontSize="10" fontFamily="monospace" fill={T.textTer}
      >No sessions.</text>
    </svg>
  );
}
